import { useCallback, useEffect, useState } from 'react';
import {
  IInvite,
  buildInviteUrl,
  invitesEndpoints,
} from '../../../endpoints/invites';
import { SelectOption } from '../../../shared/CustomFormV3/types';

/** Opzioni proposte nel form di creazione. */
export const MAX_USES_OPTIONS: SelectOption<number>[] = [
  { label: '10 atleti', value: 10 },
  { label: '25 atleti', value: 25 },
  { label: '50 atleti', value: 50 },
];

export const EXPIRY_OPTIONS: SelectOption<number>[] = [
  { label: '7 giorni', value: 7 },
  { label: '30 giorni', value: 30 },
  { label: '90 giorni', value: 90 },
];

/**
 * Gestione dei link di invito di un team: elenco, creazione, revoca e copia.
 *
 * Non usa TanStack Query perché il pannello è secondario e si apre a richiesta:
 * una fetch esplicita all'apertura è più prevedibile di una cache condivisa da
 * invalidare da più punti.
 */
export const useTeamInvites = (teamId: string, isOpen: boolean) => {
  const [invites, setInvites] = useState<IInvite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const [maxUses, setMaxUses] = useState(25);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [label, setLabel] = useState('');

  const loadInvites = useCallback(async () => {
    if (!teamId) return;
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await invitesEndpoints.list(teamId);
      setInvites(data);
    } catch {
      setError('Non è stato possibile caricare i link.');
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (isOpen) loadInvites();
  }, [isOpen, loadInvites]);

  const createInvite = useCallback(async () => {
    if (isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      await invitesEndpoints.create({
        teamId,
        maxUses,
        expiresInDays,
        ...(label.trim() && { label: label.trim() }),
      });
      setLabel('');
      await loadInvites();
    } catch {
      setError('Non è stato possibile creare il link.');
    } finally {
      setIsCreating(false);
    }
  }, [teamId, maxUses, expiresInDays, label, isCreating, loadInvites]);

  const toggleRevoke = useCallback(
    async (invite: IInvite) => {
      setError(null);
      try {
        await invitesEndpoints.revoke(invite._id, !invite.revokedAt);
        await loadInvites();
      } catch {
        setError('Non è stato possibile aggiornare il link.');
      }
    },
    [loadInvites],
  );

  const copyLink = useCallback(async (code: string) => {
    const url = buildInviteUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      // Il browser può negare la clipboard (permessi, contesto non sicuro):
      // meglio mostrare l'URL che lasciare il coach senza nulla in mano.
      window.prompt('Copia il link:', url);
    }
  }, []);

  return {
    invites,
    isLoading,
    error,
    isCreating,
    copiedCode,
    maxUses,
    expiresInDays,
    label,
    setMaxUses,
    setExpiresInDays,
    setLabel,
    createInvite,
    toggleRevoke,
    copyLink,
    reload: loadInvites,
  };
};
